// ./src/contexts/MembershipTypesContext/MembershipTypesContext.tsx
//
// Thin context wrapper around the useMembershipTypes hook.
//
// All derived data (activeItems, totalCount, hasItems, getById) lives in the
// hook itself.
//
// Usage:
//   <MembershipTypesProvider autoLoad>
//     <MyComponent />          {/* reads via useMembershipTypesContext() */}
//   </MembershipTypesProvider>
//
// Two accessors are exported:
//   useMembershipTypesContext()         — throws if no provider (for components that *require* it)
//   useOptionalMembershipTypesContext() — returns null (for components that can work with or without)

import React, { createContext, useContext } from "react";

import {
  useMembershipTypes,
  UseMembershipTypesOptions,
  UseMembershipTypesReturn,
} from "@/hooks/useMembershipTypes";

// ============================================
// Context
// ============================================

const MembershipTypesContext = createContext<UseMembershipTypesReturn | null>(null);

// ============================================
// Provider
// ============================================

export interface MembershipTypesProviderProps extends UseMembershipTypesOptions {
  children: React.ReactNode;
}

/**
 * Wraps children with a shared MembershipTypes state.
 * Each provider instance creates its own independent hook,
 * so two providers on different pages won't interfere.
 */
export const MembershipTypesProvider: React.FC<MembershipTypesProviderProps> = ({
  children,
  ...options
}) => {
  const value = useMembershipTypes(options);

  return (
    <MembershipTypesContext.Provider value={value}>
      {children}
    </MembershipTypesContext.Provider>
  );
};

// ============================================
// Strict accessor — throws when no provider
// ============================================

/**
 * Returns the MembershipTypes context value.
 * Throws if called outside a <MembershipTypesProvider>.
 */
export const useMembershipTypesContext = (): UseMembershipTypesReturn => {
  const context = useContext(MembershipTypesContext);

  if (!context) {
    throw new Error(
      "useMembershipTypesContext must be used within a <MembershipTypesProvider>. " +
      "Wrap your component tree with <MembershipTypesProvider> to fix this."
    );
  }

  return context;
};

// ============================================
// Safe accessor — returns null when no provider
// ============================================

/**
 * Returns the MembershipTypes context value, or null if no provider is present.
 * Use this in components that should work both inside and outside a provider
 * (e.g. reusable list/drawer components).
 */
export const useOptionalMembershipTypesContext =
  (): UseMembershipTypesReturn | null => {
    return useContext(MembershipTypesContext);
  };
